import { Action } from 'modules/types/common';
import queryStringParser from 'query-string';
import { identifyUser } from 'store/analytics/actions';
import { getAppSettings } from 'store/appSettings/actions';
import {
  getTermsAndConditions,
  GET_DEPS,
  GET_DEPS_ERROR,
  GET_DEPS_SUCCESS
} from 'store/global/actions';
import { getMyDoctors } from 'store/myDoctors/actions';
import { getProfile } from 'store/profile/actions';
import { getRedirectIdx, getRegisterBrand, RedirectBrand } from 'store/register';
import analyticsService, { AnalyticsEvent, AmplitudeEventData } from 'services/AnalyticsService';
import { RootDispatch } from '../types';
import {
  authenticateTPSWithAuth,
  AUTHENTICATE_USER,
  AUTHENTICATE_USER_SUCCESS,
  getToken,
  verifyToken
} from './actions';
import { getUserSession, setLoginType, setUserSession } from './actions.common';
import { LOCAL_STORAGE_TOKEN } from './configs.common';
import { LoginType } from './types';

export const bootstrapApp = () => async (dispatch: RootDispatch) => {
  const now = Date.now();
  // Save redirect index if it has it
  // Get the existing brand and redirect_index to protect against a refresh
  const registerBrand = getRegisterBrand();
  const redirectIdx = getRedirectIdx();

  const urlParams = queryStringParser.parse(window?.location?.search) || {};

  const {
    branding = registerBrand || RedirectBrand.MHP,
    code = '',
    redirect_index = redirectIdx || '000'
  } = urlParams;

  window?.sessionStorage?.setItem('registerBrand', branding as string);
  window?.sessionStorage?.setItem('redirectIdx', encodeURI(redirect_index as string));
  window?.sessionStorage?.setItem('redirectIdxTimeStamp', Date.now().toString());

  const currentUrl = window?.location?.href || '';
  dispatch({ type: AUTHENTICATE_USER });

  let localSessionKey = '';

  // Appsettings
  const appSettings = await dispatch(getAppSettings());
  const { apikey } = appSettings?.payload?.data?.shared || {};

  if (appSettings?.error || !apikey) {
    dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: {} });
    return false;
  }
  // getTermsAndConditions
  dispatch(getTermsAndConditions());
  // Handle Callback code
  if (code && currentUrl.includes('auth/ping/callback')) {
    analyticsService.logEvent(AnalyticsEvent.SignInStarted, {
      application: 'DFD',
      login_type: LoginType.PASSWORD
    });
    const dfdAuth = await dispatch(getToken(code as string));
    if (dfdAuth?.accessToken) {
      localSessionKey = dfdAuth.accessToken;
      dispatch(setLoginType(LoginType.PASSWORD));
      dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: { dfdAuth } });

      await setUserSession(dfdAuth, apikey);
      await dispatch(authenticateTPSWithAuth(localSessionKey, apikey));

      const isRedirecting = window?.sessionStorage?.getItem('tpsIsRedirecting'); // TODO CONST
      if (isRedirecting === 'TRUE') {
        // Note pause on redirecting
        return true;
      }
      return handleUserSession(
        localSessionKey,
        apikey,
        dispatch,
        LoginType.PASSWORD,
        now,
        currentUrl
      );
    }
  } else {
    // LOCAL_STORAGE_TOKEN
    window?.sessionStorage?.setItem('tpsIsRedirecting', '');
    if (localStorage?.getItem) {
      localSessionKey = localStorage.getItem(LOCAL_STORAGE_TOKEN) as string;
    }
    // getUserSession
    if (localSessionKey) {
      dispatch(setLoginType(LoginType.STORED_AUTH));
      analyticsService.logEvent(AnalyticsEvent.SignInStarted, {
        application: 'DFD',
        login_type: LoginType.STORED_AUTH
      });
      const tokenVerifyResult = await dispatch(verifyToken(localSessionKey));
      const tokenActive = !!tokenVerifyResult?.payload?.data?.active;
      if (!tokenActive) {
        // Unathenticated user
        dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: {} });
        return false;
      }
      const userSession = await getUserSession(localSessionKey, apikey);
      if (!userSession?.accessToken) {
        // Unathenticated user
        dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: {} });
        return false;
      }
      dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: { dfdAuth: userSession } });
      await dispatch(authenticateTPSWithAuth(localSessionKey, apikey));
      return handleUserSession(
        localSessionKey,
        apikey,
        dispatch,
        LoginType.STORED_AUTH,
        now,
        currentUrl
      );
    }
  }

  analyticsService.logEvent(AnalyticsEvent.SignInGuest, { application: 'DFD' });
  // Unathenticated user
  dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: {} });
  return false;
};

/**
 * Method: handleUserSession
 * Description: Handles the user session
 */
export async function handleUserSession(
  localSessionKey: string,
  apikey: string,
  dispatch: RootDispatch,
  loginType?: LoginType,
  startTime = Date.now(),
  pathname?: string
) {
  if (localSessionKey && apikey) {
    await getDeps(dispatch);

    const endTime = Date.now();
    let loadTime = (endTime - startTime) / 1000;
    if (loadTime < 1) loadTime = 1;
    const data: AmplitudeEventData = {
      currentUrl: pathname || 'Loading',
      application: 'DFD',
      load_time: loadTime,
      login_type: loginType || LoginType.PASSWORD,
      login_success: 'success'
    };
    analyticsService.logEvent(AnalyticsEvent.SignInCompleted, data);

    return true;
  }
  // Unathenticated user
  dispatch({ type: AUTHENTICATE_USER_SUCCESS, payload: {} });
  return false;
}

/**
 * Method: getDependencies
 * Description: gets the necessary deps for an authenticated user
 */
export async function getDeps(dispatch: RootDispatch): Promise<Action> {
  dispatch({ type: GET_DEPS });
  const resetAccountAccess = window?.sessionStorage?.getItem('resetAccountAccess');
  const refresh = resetAccountAccess === 'TRUE';
  try {
    const profileRes = await dispatch(getProfile('', refresh));
    if (profileRes?.error) {
      throw profileRes.error;
    }

    const anaRes = await dispatch(identifyUser());
    if (anaRes?.error) {
      throw anaRes.error;
    }

    dispatch(getMyDoctors());

    window?.sessionStorage?.removeItem('resetAccountAccess');
    return dispatch({ type: GET_DEPS_SUCCESS });
  } catch (error) {
    window?.sessionStorage?.removeItem('resetAccountAccess');
    return dispatch({ type: GET_DEPS_ERROR, error });
  }
}
